import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileText, Table } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data?: unknown;
  timeRange: string;
}

export function ExportDialog({ open, onOpenChange, timeRange }: ExportDialogProps) {
  const [exportFormat, setExportFormat] = useState('csv');
  const [selectedData, setSelectedData] = useState({
    overview: true,
    performance: true,
    players: true,
    sessions: true,
    technical: false
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const filename = `sixsight-analytics-${timeRange}-${Date.now()}.${exportFormat}`;
      
      toast({
        title: 'Export successful',
        description: `Analytics data has been exported as ${filename}`,
      });
      
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Export failed',
        description: 'There was an error exporting your data. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDataSelection = (key: string, checked: boolean) => {
    setSelectedData(prev => ({ ...prev, [key]: checked }));
  };

  const dataOptions = [
    { key: 'overview', label: 'Overview Statistics', description: 'Basic metrics and totals' },
    { key: 'performance', label: 'Performance Metrics', description: 'Quality and accuracy trends' },
    { key: 'players', label: 'Player Data', description: 'Individual player statistics' },
    { key: 'sessions', label: 'Session Data', description: 'Training session details' },
    { key: 'technical', label: 'Technical Analysis', description: 'Shot types and technical metrics' }
  ];

  const selectedCount = Object.values(selectedData).filter(Boolean).length;
  const estimatedSize = selectedCount * 0.5; // MB estimate

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Download className="mr-2 h-5 w-5" />
            Export Analytics Data
          </DialogTitle>
          <DialogDescription>
            Select the data you want to export and choose your preferred format.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Export Format */}
          <div className="space-y-2">
            <Label>Export Format</Label>
            <Select value={exportFormat} onValueChange={setExportFormat}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">
                  <div className="flex items-center">
                    <Table className="mr-2 h-4 w-4" />
                    CSV (Comma Separated Values)
                  </div>
                </SelectItem>
                <SelectItem value="json">
                  <div className="flex items-center">
                    <FileText className="mr-2 h-4 w-4" />
                    JSON (JavaScript Object Notation)
                  </div>
                </SelectItem>
                <SelectItem value="xlsx">
                  <div className="flex items-center">
                    <Table className="mr-2 h-4 w-4" />
                    Excel (XLSX)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Data Selection */}
          <div className="space-y-3">
            <Label>Data to Export</Label>
            <div className="space-y-3">
              {dataOptions.map((option) => (
                <div key={option.key} className="flex items-start space-x-3">
                  <Checkbox
                    id={option.key}
                    checked={selectedData[option.key as keyof typeof selectedData]}
                    onCheckedChange={(checked) => 
                      handleDataSelection(option.key, checked as boolean)
                    }
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label
                      htmlFor={option.key}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {option.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {option.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Export Summary */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex justify-between text-sm">
              <span>Selected datasets:</span>
              <span className="font-medium">{selectedCount}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Estimated size:</span>
              <span className="font-medium">{estimatedSize.toFixed(1)} MB</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Time range:</span>
              <span className="font-medium">{timeRange}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExport}
            disabled={selectedCount === 0 || isExporting}
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export Data
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}